import { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

import Tab from "@/components/tab/Tab"
import BasicButton from "@/components/button/BasicButton"
import JumpActionTab from "@/app/settings/metric/actions/JumpActionTab"
import DiveActionTab from "@/app/settings/metric/actions/DiveActionTab"
import CODActionTab from "@/app/settings/metric/actions/CODActionTab"
import ActionSettingTab from "@/app/settings/metric/actions/ActionSettingTab"
import { ActionSettingType } from "@/constants/unit"
import {
  ReadActionsResponse,
  ReadJumpActionResponse,
  ReadCODActionResponse,
  ReadDiveActionResponse,
} from "@/types/setting"
import { createActionSettingState } from "@/stores/settings"
import { DEFAULT_ACTIONS_VALUE } from "./constants"
import { createGlobalStore } from "@/stores/global"
import { useNativeBridge } from "@/hooks/useNativeBridge"
import {
  useRunActionQuery,
  useAccelActionQuery,
  useDecelActionQuery,
  useJumpActionQuery,
  useDiveActionQuery,
  useCODActionQuery,
  useUpsertRunActionMutation,
  useUpsertAccelActionMutation,
  useUpsertDecelActionMutation,
  useUpsertJumpActionMutation,
  useUpsertDiveActionMutation,
  useUpsertCODActionMutation,
} from "@/hooks/api/useSettingQuery"
import {
  useSpeedZonesQuery,
  useAccelZonesQuery,
  useDecelZonesQuery,
} from "@/hooks/api/useZoneQuery"

import "./actions.scss"
import { createAccountStore } from "@/stores/account"

export default function Actions() {
  const { t } = useTranslation()
  const { isAllValid, hasChanges, setHasChanges } = createActionSettingState()
  const setSnackBar = createGlobalStore((state) => state.setSnackBar)
  const { notifySettingChanged } = useNativeBridge()
  const accessTestFeature = createAccountStore(
    (state) => state.teamProfileInfo?.teamPermissions?.ACCESS_TEST_FEATURE,
  )

  const { data: runData, refetch: refetchRun } = useRunActionQuery({ enabled: true })
  const { data: accelData, refetch: refetchAccel } = useAccelActionQuery({ enabled: true })
  const { data: decelData, refetch: refetchDecel } = useDecelActionQuery({ enabled: true })
  const { data: jumpData, refetch: refetchJump } = useJumpActionQuery()
  const { data: diveData, refetch: refetchDive } = useDiveActionQuery()
  const { data: codData, refetch: refetchCod } = useCODActionQuery()

  const { data: speedZones } = useSpeedZonesQuery({ enabled: true })
  const { data: accelZones } = useAccelZonesQuery({ enabled: true })
  const { data: decelZones } = useDecelZonesQuery({ enabled: true })

  const { mutateAsync: runMutateAsync } = useUpsertRunActionMutation()
  const { mutateAsync: accelMutateAsync } = useUpsertAccelActionMutation()
  const { mutateAsync: decelMutateAsync } = useUpsertDecelActionMutation()
  const { mutateAsync: jumpMutateAsync } = useUpsertJumpActionMutation()
  const { mutateAsync: diveMutateAsync } = useUpsertDiveActionMutation()
  const { mutateAsync: codMutateAsync } = useUpsertCODActionMutation()

  const [tabValue, setTabValue] = useState<ActionSettingType>(ActionSettingType.RUN)
  const [allStateLoading, setAllStateLoading] = useState(false)
  const [tabStates, setTabStates] = useState<{
    [key in ActionSettingType]:
      | ReadActionsResponse
      | ReadJumpActionResponse
      | ReadDiveActionResponse
      | ReadCODActionResponse
      | null
  }>(DEFAULT_ACTIONS_VALUE)

  useEffect(() => {
    if (runData && !tabStates.run) {
      setTabStates((prev) => ({ ...prev, run: runData }))
    }
  }, [runData])

  useEffect(() => {
    if (accelData && !tabStates.acceleration) {
      setTabStates((prev) => ({ ...prev, acceleration: accelData }))
    }
  }, [accelData])

  useEffect(() => {
    if (decelData && !tabStates.deceleration) {
      setTabStates((prev) => ({ ...prev, deceleration: decelData }))
    }
  }, [decelData])

  useEffect(() => {
    if (jumpData && !tabStates.jump) {
      setTabStates((prev) => ({ ...prev, jump: jumpData }))
    }
  }, [jumpData])

  useEffect(() => {
    if (diveData && !tabStates.dive) {
      setTabStates((prev) => ({ ...prev, dive: diveData }))
    }
  }, [diveData])

  useEffect(() => {
    if (codData && !tabStates.cod) {
      setTabStates((prev) => ({ ...prev, cod: codData }))
    }
  }, [codData])

  const tabContent = useMemo(() => {
    switch (tabValue) {
      case ActionSettingType.RUN:
        return (
          <ActionSettingTab
            type={ActionSettingType.RUN}
            initialData={runData}
            zones={speedZones}
            editedData={tabStates.run as ReadActionsResponse}
            setEditedData={(state: ReadActionsResponse) =>
              setTabStates((prev) => ({
                ...prev,
                run: state,
              }))
            }
          />
        )
      case ActionSettingType.ACCELERATION:
        return (
          <ActionSettingTab
            type={ActionSettingType.ACCELERATION}
            initialData={accelData}
            zones={accelZones}
            editedData={tabStates.acceleration as ReadActionsResponse}
            setEditedData={(state: ReadActionsResponse) =>
              setTabStates((prev) => ({
                ...prev,
                acceleration: state,
              }))
            }
          />
        )
      case ActionSettingType.DECELERATION:
        return (
          <ActionSettingTab
            type={ActionSettingType.DECELERATION}
            initialData={decelData}
            zones={decelZones}
            editedData={tabStates.deceleration as ReadActionsResponse}
            setEditedData={(state: ReadActionsResponse) =>
              setTabStates((prev) => ({
                ...prev,
                deceleration: state,
              }))
            }
          />
        )
      case ActionSettingType.JUMP:
        return (
          <JumpActionTab
            initialData={jumpData}
            editedData={tabStates.jump as ReadJumpActionResponse}
            setEditedData={(state: ReadJumpActionResponse) =>
              setTabStates((prev) => ({
                ...prev,
                jump: state,
              }))
            }
          />
        )
      case ActionSettingType.DIVE:
        return (
          <DiveActionTab
            initialData={diveData}
            editedData={tabStates.dive as ReadDiveActionResponse}
            setEditedData={(state: ReadDiveActionResponse) =>
              setTabStates((prev) => ({
                ...prev,
                dive: state,
              }))
            }
          />
        )
      case ActionSettingType.COD:
        return (
          <CODActionTab
            initialData={codData}
            editedData={tabStates.cod as ReadCODActionResponse}
            setEditedData={(state: ReadCODActionResponse) =>
              setTabStates((prev) => ({
                ...prev,
                cod: state,
              }))
            }
          />
        )
      default:
        return <></>
    }
  }, [
    tabValue,
    tabStates,
    runData,
    accelData,
    decelData,
    jumpData,
    diveData,
    codData,
    speedZones,
    accelZones,
    decelZones,
  ])

  const onSaveAll = async () => {
    const saves: Promise<unknown>[] = []
    const savedKeys: ActionSettingType[] = []
    setAllStateLoading(true)
    notifySettingChanged("action", "saving")

    if (hasChanges[ActionSettingType.RUN] && tabStates.run) {
      const data = tabStates.run as ReadActionsResponse
      saves.push(runMutateAsync({ global: data.global }))
      savedKeys.push(ActionSettingType.RUN)
    }

    if (hasChanges[ActionSettingType.ACCELERATION] && tabStates.acceleration) {
      const data = tabStates.acceleration as ReadActionsResponse
      saves.push(accelMutateAsync({ global: data.global }))
      savedKeys.push(ActionSettingType.ACCELERATION)
    }

    if (hasChanges[ActionSettingType.DECELERATION] && tabStates.deceleration) {
      const data = tabStates.deceleration as ReadActionsResponse
      saves.push(decelMutateAsync({ global: data.global }))
      savedKeys.push(ActionSettingType.DECELERATION)
    }

    if (hasChanges[ActionSettingType.JUMP] && tabStates.jump) {
      const data = tabStates.jump as ReadJumpActionResponse
      saves.push(jumpMutateAsync({ global: data.global }))
      savedKeys.push(ActionSettingType.JUMP)
    }

    if (hasChanges[ActionSettingType.DIVE] && tabStates.dive) {
      const data = tabStates.dive as ReadDiveActionResponse
      saves.push(diveMutateAsync({ global: data.global }))
      savedKeys.push(ActionSettingType.DIVE)
    }

    if (hasChanges[ActionSettingType.COD] && tabStates.cod) {
      const data = tabStates.cod as ReadCODActionResponse
      saves.push(codMutateAsync({ global: data.global }))
      savedKeys.push(ActionSettingType.COD)
    }

    try {
      await Promise.all(saves)
      setSnackBar({ message: t("messages.successUpdate"), open: true, severity: "success" })
      notifySettingChanged("action", "update")

      const refetchPromises: Promise<unknown>[] = []

      if (savedKeys.includes(ActionSettingType.RUN)) {
        refetchPromises.push(refetchRun())
      }
      if (savedKeys.includes(ActionSettingType.ACCELERATION)) {
        refetchPromises.push(refetchAccel())
      }
      if (savedKeys.includes(ActionSettingType.DECELERATION)) {
        refetchPromises.push(refetchDecel())
      }
      if (savedKeys.includes(ActionSettingType.JUMP)) {
        refetchPromises.push(refetchJump())
      }
      if (savedKeys.includes(ActionSettingType.DIVE)) {
        refetchPromises.push(refetchDive())
      }
      if (savedKeys.includes(ActionSettingType.COD)) {
        refetchPromises.push(refetchCod())
      }

      await Promise.all(refetchPromises)

      setTabStates((prev) => {
        const newStates = { ...prev }
        savedKeys.forEach((key) => {
          newStates[key] = null
        })

        return newStates
      })

      savedKeys.forEach((key) => {
        setHasChanges(key, false)
      })
    } catch (error) {
      console.error("Error saving all actions:", error)
      notifySettingChanged("action", "error")
    } finally {
      setAllStateLoading(false)
    }
  }

  return (
    <div className="ActionSettingCont">
      <Tab
        type="filled"
        list={Object.values(ActionSettingType)
          .filter((type) => accessTestFeature || type !== ActionSettingType.COD)
          .map((type) => ({
            label: t(type),
            value: type,
            disabled: false,
          }))}
        value={tabValue}
        onChange={(_, newVal) => setTabValue(newVal)}
      />

      <div className="ActionRowsCont">{tabContent}</div>

      <div className="ActionButtonCont">
        <BasicButton
          type="primary-contained"
          size="lg"
          onClick={onSaveAll}
          disabled={
            allStateLoading ||
            !Object.values(isAllValid).every(Boolean) ||
            !Object.values(hasChanges).some(Boolean)
          }>
          {t("button.save")}
        </BasicButton>
      </div>
    </div>
  )
}
